import Datatable from "@/components/tables/datatable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCreateDepositBonus,
  useGetAllDepositBonuses,
  useGetDepositBonusById,
  useUpdateDepositBonus,
} from "@/services/depositbonus.service";
import {
  DatePicker,
  Tab,
  Tabs,
  Textarea,
  getKeyValue,
} from "@nextui-org/react";
import { format, isBefore } from "date-fns";
import { Key, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { DepositBonusClaimList } from "./claim";
import { MultiSelect } from "@/components/ui/multi-select";
import { BackArrowIcon, EditIcon } from "@/assets/icons/admin";
import { fromDate, getLocalTimeZone } from "@internationalized/date";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import games from "../../../assets/games.json";

export const DepositBonusModuleTabs = () => {
  return (
    <div className="flex w-full flex-col justify-evenly">
      <Tabs
        variant="underlined"
        aria-label="Player Module"
        className="w-full justify-start"
        classNames={{
          cursor: ["user-module-selected", ""],
          tabContent: ["text-base font-bold"],
        }}
      >
        <Tab key="list" title="List">
          <DepositBonusList />
        </Tab>
        <Tab key="claims" title="Claims">
          <DepositBonusClaimList />
        </Tab>
      </Tabs>
    </div>
  );
};

const columns: any[] = [
  {
    key: "name",
    label: "Bonus Name",
  },
  {
    key: "code",
    label: "Bonus Code",
  },
  {
    key: "startTime",
    label: "Start Time",
  },
  {
    key: "endTime",
    label: "End Time",
  },
  {
    key: "maxBonusAmount",
    label: "Max Bonus ($)",
  },
  {
    key: "claimLimit",
    label: "Claim Limit",
  },
  {
    key: "percentage",
    label: "Bonus Percentage",
  },
  {
    key: "releaseMultiplier",
    label: "Wager Multiplier",
  },
  {
    key: "status",
    label: "Status",
  },
  {
    key: "actions",
    label: "Actions",
  },
];

const DepositBonusList = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(undefined);
  const [refetch, setIsRefetch] = useState(false);

  const query = useGetAllDepositBonuses;

  const renderCell = useCallback((data: any[], item: any, columnKey: Key) => {
    switch (columnKey) {
      case "startTime":
      case "endTime":
        return (
          <div className="font-normal">
            {format(
              getKeyValue(item, columnKey as string),
              "dd/MM/yyyy HH:mm:ss",
            ) || "-"}
          </div>
        );

      case "percentage":
        return (
          <div className="font-normal">
            {parseFloat(getKeyValue(item, columnKey as string) || "0") * 100}%
          </div>
        );

      case "releaseMultiplier":
        return (
          <div className="font-normal">
            {getKeyValue(item, columnKey as string) || "0"}X
          </div>
        );

      case "actions":
        return (
          <div className="font-normal">
            <div
              className="hover:cursor-pointer"
              onClick={() => {
                setSelectedId(getKeyValue(item, "id"));
                setIsEditOpen(true);
              }}
            >
              <EditIcon />
            </div>
          </div>
        );

      default:
        return (
          <div className="font-normal">
            {getKeyValue(item, columnKey as string) || "-"}
          </div>
        );
    }
  }, []);

  if (isEditOpen) {
    return (
      <EditDepositBonus
        id={selectedId}
        onClose={() => {
          setIsEditOpen(false);
          setIsRefetch(true);
        }}
      />
    );
  }

  return (
    <div>
      <Datatable
        columns={columns}
        renderCell={renderCell}
        query={query}
        triggerRefetch={refetch}
        customQueryFilter={{}}
        customFilters={[]}
        customActions={[
          <Button
            onClick={() => {
              setIsEditOpen(true), setSelectedId(undefined);
            }}
            variant="default"
            className="bg-button-gradient text-white"
          >
            Add New
          </Button>,
        ]}
        searchFields={[
          {
            label: "Bonus Name",
            value: "name",
          },
        ]}
        onRefetch={() => setIsRefetch(false)}
      />
    </div>
  );
};

interface EditDepositBonusProps {
  id?: string;
  onClose: () => void;
}

const depositBonusFormSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, "Required").max(32, "Maximum is 32 charater"),
  code: z.string().min(1, "Required").max(12, "Maximum is 12 charater"),
  description: z.string().optional(),
  maxBonusAmount: z.coerce.number().nonnegative(),
  percentage: z.coerce.number().nonnegative(),
  claimLimit: z.coerce.number().nonnegative(),
  releaseMultiplier: z.coerce.number().nonnegative(),
});

type DepositBonusFormData = z.infer<typeof depositBonusFormSchema>;

const gamesData = games.map((x) => {
  return {
    value: x.id,
    label: x.name,
  };
});

const EditDepositBonus = ({ id, onClose }: EditDepositBonusProps) => {
  const { mutate: create } = useCreateDepositBonus();
  const { mutate: update } = useUpdateDepositBonus();
  const { data: depositBonusData, isSuccess } = useGetDepositBonusById(
    id ?? "",
  );

  const { getValues, setValue, formState, handleSubmit, register } =
    useForm<DepositBonusFormData>({
      resolver: zodResolver(depositBonusFormSchema),
    });
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedGames, setSelectedGames] = useState<string[]>([]);

  useEffect(() => {
    if (isSuccess) {
      const {
        id,
        name,
        code,
        description,
        startTime,
        endTime,
        maxBonusAmount,
        percentage,
        claimLimit,
        releaseMultiplier,
        categories,
        status,
        gameIds,
      } = depositBonusData;

      setValue("id", id);
      setValue("name", name);
      setValue("code", code);
      setValue("description", description);
      startTime && setStartDate(new Date(startTime));
      endTime && setEndDate(new Date(endTime));
      setValue("maxBonusAmount", maxBonusAmount);
      setValue("percentage", percentage * 100);
      setValue("claimLimit", claimLimit);
      setValue("releaseMultiplier", releaseMultiplier);
      status && setSelectedStatus(status);
      gameIds && setSelectedGames(gameIds);
    }
  }, [isSuccess]);

  const onSubmit = () => {
    if (
      isBefore(endDate, startDate) ||
      startDate.getTime() === endDate.getTime()
    ) {
      toast.error("End date can't be less or equal to the start date");
      return;
    }

    if (id) {
      update(
        {
          id,
          name: getValues("name"),
          code: getValues("code"),
          startTime: startDate?.toISOString() ?? new Date().toISOString(),
          endTime: endDate?.toISOString() ?? new Date().toISOString(),
          description: getValues("description") ?? "",
          maxBonusAmount: getValues("maxBonusAmount"),
          percentage: getValues("percentage"),
          claimLimit: getValues("claimLimit"),
          releaseMultiplier: getValues("releaseMultiplier"),
          categories: "all",
          status: selectedStatus,
          gameIds: selectedGames,
        },
        {
          onSuccess: (res) => {
            toast.success("Bonus has been updated");
          },
          onError: () => {
            toast.error("There is a problem saving the bonus");
          },
        },
      );
    } else {
      create(
        {
          name: getValues("name"),
          code: getValues("code"),
          startTime: startDate?.toISOString() ?? new Date().toISOString(),
          endTime: endDate?.toISOString() ?? new Date().toISOString(),
          description: getValues("description") ?? "",
          maxBonusAmount: getValues("maxBonusAmount"),
          percentage: getValues("percentage"),
          claimLimit: getValues("claimLimit"),
          releaseMultiplier: getValues("releaseMultiplier"),
          categories: "all",
          status: selectedStatus,
          gameIds: selectedGames,
        },
        {
          onSuccess: () => {
            toast.success("Bonus has been created");
          },
          onError: () => {
            toast.error("There is a problem saving the bonus");
          },
        },
      );
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-row items-center gap-2">
        <div className="hover:cursor-pointer" onClick={onClose}>
          <BackArrowIcon />
        </div>
        <h4>{id ? "Edit" : "Add"} Deposit Bonus</h4>
      </div>
      <form className="flex flex-col gap-8" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">
          <span>General Information</span>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label>Bonus Name</Label>
              <Input
                id="name"
                {...register("name")}
                endAdornment={
                  <div className="mr-4 text-xs text-danger">
                    {formState?.errors?.name?.message}
                  </div>
                }
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>Bonus Code</Label>
              <Input
                id="code"
                {...register("code")}
                endAdornment={
                  <div className="mr-4 text-xs text-danger">
                    {formState?.errors?.code?.message}
                  </div>
                }
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>Start Time</Label>
              <DatePicker
                className="input-bg rounded-md border-1 border-white"
                showMonthAndYearPickers
                classNames={{
                  timeInput: "text-blue data-[editable=true]:text-primary",
                }}
                granularity="second"
                value={fromDate(startDate, getLocalTimeZone()) as any}
                onChange={(val: any) => {
                  setStartDate(val?.toDate() ?? new Date());
                }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>End Time</Label>
              <DatePicker
                className="input-bg rounded-md border-1 border-white text-white"
                showMonthAndYearPickers
                classNames={{
                  timeInput: "text-blue data-[editable=true]:text-primary",
                }}
                granularity="second"
                value={fromDate(endDate, getLocalTimeZone()) as any}
                onChange={(val: any) => {
                  setEndDate(val?.toDate() ?? new Date());
                }}
              />
            </div>
            <div className="col-span-full flex flex-col gap-2">
              <Label>Description</Label>
              <Textarea
                className="input-bg rounded-md border-1 border-white"
                id="description"
                {...register("description")}
                endContent={
                  <div className="mr-4 mt-1 text-xs text-danger">
                    {formState?.errors?.description?.message}
                  </div>
                }
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <span>Details</span>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label>Max Bonus</Label>
              <Input
                type="number"
                id="maxBonusAmount"
                {...register("maxBonusAmount", { valueAsNumber: true })}
                endAdornment={
                  <div className="mr-4 text-xs text-danger">
                    {formState?.errors?.maxBonusAmount?.message}
                  </div>
                }
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label>Redemption Limit</Label>
              <Input
                type="number"
                id="claimLimit"
                {...register("claimLimit", { valueAsNumber: true })}
                endAdornment={
                  <div className="mr-4 text-xs text-danger">
                    {formState?.errors?.claimLimit?.message}
                  </div>
                }
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label>Bonus Percentage</Label>
              <Input
                type="number"
                id="percentage"
                {...register("percentage", { valueAsNumber: true })}
                endAdornment={
                  <div className="mr-4 text-xs text-danger">
                    {formState?.errors?.percentage?.message}
                  </div>
                }
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label>Wager Multiplier</Label>
              <Input
                type="number"
                id="releaseMultiplier"
                {...register("releaseMultiplier", { valueAsNumber: true })}
                endAdornment={
                  <div className="mr-4 text-xs text-danger">
                    {formState?.errors?.releaseMultiplier?.message}
                  </div>
                }
              />
            </div>

            <div className="flex flex-col gap-2">
              <div>
                <Label>Status</Label>
                <Select
                  defaultValue={selectedStatus}
                  onValueChange={(e) => {
                    setSelectedStatus(e);
                  }}
                >
                  <SelectTrigger className="input-bg text-start">
                    <SelectValue placeholder="Select Status"></SelectValue>
                  </SelectTrigger>
                  <SelectContent className="text-start">
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div>
                <Label>Games</Label>
                <MultiSelect
                  options={gamesData}
                  onValueChange={setSelectedGames}
                  placeholder="Select Games"
                  variant="inverted"
                  animation={0}
                  maxCount={6}
                  selectedValues={selectedGames}
                />
              </div>
            </div>
          </div>
        </div>
        <Button
          className="bg-button-gradient text-white"
          type="submit"
          variant="default"
        >
          Save
        </Button>
      </form>
    </div>
  );
};
